import styled from '@emotion/styled'
import {
  MdErrorOutline as ErrorIcon,
  MdInfoOutline as InfoIcon,
  MdCheckCircle as CheckIcon
} from 'react-icons/md'
import { stateColors } from 'shop/theme/defaultTheme'
import { StyledHTMLElement } from 'shop/theme/types'
import { CiWarning } from 'react-icons/ci'

type Positions = 'top' | 'bottom'
type Skin = 'regular' | 'mild' | 'warning'
type Type = 'warning' | 'error' | 'info' | 'success'

interface Props {
  heading?: string
  subheading?: string
  skin?: Skin
  type?: Type
  flash?: boolean
  position?: Positions | { desktop: Positions; mobile: Positions }
}

const AlertMessage = ({
  heading,
  subheading,
  type = 'warning',
  skin = 'regular',
  flash,
  position = 'top'
}: Props) => {
  return (
    <Container {...{ type, flash, position, skin }} data-testid='alertMessage'>
      <Icon {...{ type, skin }}>
        {type === 'warning' && <CiWarning />}
        {type === 'error' && <ErrorIcon />}
        {type === 'success' && <CheckIcon />}
        {type === 'info' && <InfoIcon />}
      </Icon>

      <Text {...{ skin }}>
        {heading && <strong>{heading}</strong>}
        {subheading && <p>{subheading}</p>}
      </Text>
    </Container>
  )
}

const Container = styled.div<{
  skin: Skin
  type: Type
  flash: boolean
  position: any
  // @ts-ignore-next-line
}>(({ theme, type, flash, position, skin }) => {
  const props = {
    lineHeight: '1em',
    marginBottom: '16px',
    display: 'flex'
  }

  const skinStyles = {
    regular: {
      boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px',
      padding: '16px',
      borderLeft: `6px solid ${stateColors[type][5]}`
    },
    mild: {
      backgroundColor: '#fafafa',
      border: '1px solid #f0f0f0',
      borderRadius: theme.borderRadius,
      padding: '8px 16px'
    },
    warning: {
      backgroundColor: '#fffcf6',
      border: '1px solid #d46b08',
      borderRadius: '6px',
      padding: '8px 12px'
    }
  }

  const flashStyles = {
    boxShadow: '0px 2px 24px rgba(0, 0, 0, 0.2)',
    padding: '8px',
    color: 'white',
    zIndex: 5000,
    position: 'fixed',
    background: 'white',
    top: 'unset',
    bottom: 'unset',
    [position?.mobile || position]: '24px',
    marginBottom: 0,
    left: '8px',
    right: '8px',
    [theme.mediaQueries.viewport7]: {
      top: 'unset',
      bottom: 'unset',
      right: 'unset',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '400px',
      [position?.desktop || position]: '16px',
      padding: '16px'
    }
  }

  return {
    ...props,
    ...skinStyles[skin],
    ...(flash ? flashStyles : {})
  }
})

const Icon = styled.div<{ skin: Skin; type: Type }>(({ theme, type, skin }) => {
  const skinStyles = {
    regular: {
      color: stateColors[type] ? stateColors[type][7] : 'black'
    },
    mild: {
      color: '#262626'
    },
    warning: {
      color: '#d46b08',
      fontSize: '24px'
    }
  }

  const props = {
    fontSize: theme.fontSizes[5],
    margin: 'auto 0',
    marginRight: '12px'
  }

  return {
    ...props,
    ...skinStyles[skin]
  }
})

const Text = styled.div<StyledHTMLElement & { skin: Skin }>(
  ({ theme, skin }) => {
    const skinStyles = {
      regular: {
        color: theme.colors.textBody
      },
      mild: {
        color: '#4a4a68'
      },
      warning: {
        color: '#d46b08'
      }
    }

    return {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      fontSize: skin === 'warning' ? '14px' : theme.fontSizes[0],
      '> p': {
        marginTop: '4px',
        marginBottom: 0
      },
      '*:only-child': {
        margin: 0
      },
      ...skinStyles[skin]
    }
  }
)

export default AlertMessage
